import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Spin } from 'antd';
import { fetchCurrentUser, fetchUserPermissions } from '../../store/slices/userSlice';
import { getToken, getUserInfo } from '../../utils/auth';

/**
 * 路由权限守卫组件
 * @param {React.ReactNode} children - 子组件
 * @param {string|string[]} requiredPermissions - 需要的权限
 * @param {boolean} requireAll - 是否需要满足所有权限
 * @returns {React.ReactNode}
 */
const PrivateRoute = ({ 
  children, 
  requiredPermissions = null,
  requireAll = false
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const [initializing, setInitializing] = useState(true);

  const { isLoggedIn, currentUser, permissions, loading } = useSelector((state) => state.user);
 
  // 组件挂载时进行初始化检查
  useEffect(() => {
    const init = async () => {
      try {
        // 检查本地存储中是否有token
        const token = getToken();
        const userInfo = getUserInfo();
        // console.log('userInfo', userInfo);
        if (token) {
          // 如果有token但没有用户信息，获取用户信息
          if (!currentUser) {
            await dispatch(fetchCurrentUser()).unwrap();
          }
          
          // 如果有token但没有权限信息，获取权限
          if (!permissions || permissions.length === 0) {
            let retryCount = 0;
            const MAX_RETRY = 1; // 最大重试次数改为1次
            
            while (retryCount < MAX_RETRY) {
              try {
                const permissionsResult = await dispatch(fetchUserPermissions(true)).unwrap();
                
                // 检查权限是否成功获取
                if (permissionsResult && permissionsResult.length > 0) {
                  console.log('获取权限成功:', permissionsResult);
                  break; // 成功获取权限，退出重试循环
                }
                
                throw new Error('获取的权限为空');
              } catch (permError) {
                retryCount++;
                console.error(`获取权限失败，重试 ${retryCount}/${MAX_RETRY}:`, permError);
                
                // 最后一次重试仍失败，显示错误提示并跳转到登录页
                if (retryCount === MAX_RETRY) {
                  // 清除无效的token和用户信息
                  localStorage.removeItem('token');
                  localStorage.removeItem('userInfo');
                  localStorage.removeItem('userPermissions');
                  
                  // 跳转到登录页
                  // window.location.href = '/login';
                  break;
                }
                
                // 添加短暂延迟，避免快速重复请求
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
          }
        }
      } catch (error) {
        console.error('初始化失败:', error);
        
        // 清除无效的token和用户信息
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        localStorage.removeItem('userPermissions');
        
        // 跳转到登录页
        window.location.href = '/login';
      } finally {
        setInitializing(false);
      }
    };
    
    init();
  }, []);
  
  // 如果正在初始化或加载中，显示加载状态
  if (initializing || loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }
  
  // 如果未登录，重定向到登录页
  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // 如果没有指定权限要求，则直接显示
  if (!requiredPermissions) {
    return children;
  }
  
  // 检查是否有权限访问
  const hasPermission = () => {
    // 超级管理员拥有所有权限
    if (permissions?.includes('*')) {
      return true;
    }
    
    // 如果是数组，检查是否拥有所有/任一权限
    if (Array.isArray(requiredPermissions)) {
      if (requireAll) {
        // 需要拥有所有指定权限
        return requiredPermissions.every(perm => permissions?.includes(perm));
      } else {
        // 只需要拥有任一指定权限
        return requiredPermissions.some(perm => permissions?.includes(perm));
      }
    }
    
    // 单个权限检查
    return permissions?.includes(requiredPermissions);
  };
  
  // 如果没有权限，重定向到403页面
  if (!hasPermission()) {
    return <Navigate to="/403" replace />;
  }
  
  // 有权限则渲染子组件
  return children;
};

export default PrivateRoute; 